import type { EnvironmentalCircularityResponse } from '@siliconx/shared';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { BasisTag } from '../BasisTag';
import { RegulationChip } from '../RegulationChip';
import { formatCarbonMass } from './circularityFormat';
import {
  CIRCULARITY_AVOIDED_VS_NEW_BASELINE,
  CIRCULARITY_AVOIDED_VS_NEW_LABEL,
  CIRCULARITY_CAT5_BURDEN_LABEL,
  CIRCULARITY_COMPARISON_TITLE,
  CIRCULARITY_COMPONENT_HARVEST_BASELINE,
  CIRCULARITY_COMPONENT_HARVEST_LABEL,
  CIRCULARITY_DISPOSAL_COLUMN_CAPTION,
  CIRCULARITY_DISPOSAL_COLUMN_LABEL,
  CIRCULARITY_NO_AVOIDED_FIGURE_NOTE,
  CIRCULARITY_REPURPOSE_BASELINE,
  CIRCULARITY_REPURPOSE_COLUMN_LABEL,
  CIRCULARITY_RESALE_COLUMN_LABEL,
} from './circularityCopy';

type Comparison = EnvironmentalCircularityResponse['comparison'];

/**
 * SIL-473 — three-column disposition comparison for a representative
 * (median-embodied) gpu cohort actually owned by the org — Resale |
 * Repurpose | Disposal. Every figure is REAL-derived, tagged
 * 'platform_default' (the SIL-420 org config scalars drive every formula
 * here; see `apps/api/src/routes/client/environmental.ts`'s `/circularity`
 * handler). The word "credit" never appears on this surface; Disposal
 * carries NO avoided/displaced number by design (SIL-459 register).
 */
export function DispositionComparison({ comparison }: { comparison: Comparison }) {
  const { cohort } = comparison;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <SectionLabel>{CIRCULARITY_COMPARISON_TITLE}</SectionLabel>
        {cohort && (
          <span className="text-xs text-sky-900/60">
            {cohort.manufacturerName} {cohort.displayName} · {formatCarbonMass(cohort.embodiedKgco2e)} embodied
          </span>
        )}
      </div>

      {!cohort ? (
        <p className="rounded-lg border border-dashed border-sky-300 bg-white/50 p-4 text-xs text-sky-900/60">
          No gpu-class asset with a resolved SKU embodied figure yet — this comparison needs at least one to pick a
          representative cohort.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ComparisonColumn
            label={CIRCULARITY_RESALE_COLUMN_LABEL}
            basis={comparison.resale.basis}
            rows={[
              {
                label: CIRCULARITY_AVOIDED_VS_NEW_LABEL,
                value: formatCarbonMass(comparison.resale.avoidedVsNewKgco2e),
                caption: CIRCULARITY_AVOIDED_VS_NEW_BASELINE,
              },
            ]}
          />
          <ComparisonColumn
            label={CIRCULARITY_REPURPOSE_COLUMN_LABEL}
            basis={comparison.repurpose.basis}
            rows={[
              {
                label: 'Avoided vs new equivalent',
                value: formatCarbonMass(comparison.repurpose.avoidedKgco2e),
                caption: CIRCULARITY_REPURPOSE_BASELINE,
              },
              {
                label: CIRCULARITY_COMPONENT_HARVEST_LABEL,
                value: formatCarbonMass(comparison.repurpose.componentHarvest.newComponentEmbodiedKgco2e),
                caption: CIRCULARITY_COMPONENT_HARVEST_BASELINE,
                basis: comparison.repurpose.componentHarvest.basis,
                sub: true,
                // SIL-611: the only regulation-chip row on this card — the
                // embedded-battery rule governs component-harvest specifically
                // (its register row names "the component-harvest and
                // repurpose paths"). Never on the Resale/Repurpose "avoided
                // vs new" rows above/below it — those aren't disposal/EOL
                // flows.
                regulationKey: 'calrecycle_battery',
              },
            ]}
          />
          <ComparisonColumn
            label={CIRCULARITY_DISPOSAL_COLUMN_LABEL}
            basis={comparison.disposal.basis}
            caption={CIRCULARITY_DISPOSAL_COLUMN_CAPTION}
            rows={[
              {
                label: CIRCULARITY_CAT5_BURDEN_LABEL,
                value: formatCarbonMass(comparison.disposal.cat5BurdenKgco2e),
                caption: CIRCULARITY_NO_AVOIDED_FIGURE_NOTE,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

interface ComparisonRow {
  label: string;
  value: string;
  caption: string;
  basis?: Comparison['resale']['basis'];
  sub?: boolean;
  /** SIL-611 — regulation chip for this row, if any (component-harvest only). */
  regulationKey?: 'universal_waste_battery' | 'ca_uwed' | 'calrecycle_battery';
}

function ComparisonColumn({
  label,
  basis,
  caption,
  rows,
}: {
  label: string;
  basis: Comparison['resale']['basis'];
  caption?: string;
  rows: ComparisonRow[];
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-sky-200 bg-white/70 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-sky-950">{label}</span>
        <BasisTag basis={basis} />
      </div>
      {caption && <p className="text-xs leading-relaxed text-sky-900/60">{caption}</p>}
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className={row.sub ? 'border-t border-sky-100 pt-2' : undefined}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-1 text-xs text-sky-900/80">
                {row.label}
                {row.basis && <BasisTag basis={row.basis} />}
                {row.regulationKey && <RegulationChip regulationKey={row.regulationKey} />}
              </span>
              <span className="font-mono text-sm font-semibold tabular-nums text-sky-700">{row.value}</span>
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-sky-900/50">{row.caption}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
